import React, {Suspense, type JSX} from 'react';
import {styled} from '@mui/material/styles';
import {ViewerScreenError} from './viewerScreenError';
import {ViewerScreenLoading} from './viewerScreenLoading';
import {ViewerScreenMessage} from './viewerScreenMessage';
import {ViewScreenDICOM} from './dicom/viewScreenDICOM';

type Props = {
  filePreviewIsLoading: boolean;
  filePreviewHasError: boolean;
  filePreviewType: string;
  filePreviewData: string;
};

const iframeStyles = {
  width: '100%',
  height: '100%',
  border: 'none',
};

const StyledPreviewWrapper = styled('div')(({theme}) => ({
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  flexGrow: 1,
  backgroundColor: theme.palette.grey['50'],
  padding: theme.spacing(1),
}));

export function FileViewer({
  filePreviewIsLoading,
  filePreviewHasError,
  filePreviewType,
  filePreviewData,
}: Readonly<Props>): JSX.Element {
  if (filePreviewIsLoading) {
    return (
      <ViewerScreenLoading
        data-testid="file-viewer-loading-state"
        message="File preview is loading"
      />
    );
  }

  if (filePreviewHasError) {
    return (
      <ViewerScreenError
        data-testid="file-viewer-error-state"
        title="File preview error"
        description="Please try to select another file and return back to this one"
      />
    );
  }

  switch (filePreviewType) {
    case 'CCDA': {
      return (
        <StyledPreviewWrapper data-testid="file-viewer-ccda">
          <iframe
            title="CCDA preview"
            style={{
              ...iframeStyles,
              pointerEvents: 'none',
            }}
            src={`data:text/html;charset=utf-8,${encodeURIComponent(filePreviewData)}`}
          />
        </StyledPreviewWrapper>
      );
    }

    case 'DICOM': {
      return (
        <StyledPreviewWrapper data-testid="file-viewer-dicom">
          <Suspense
            fallback={<ViewerScreenLoading message="Loading DICOM..." />}
          >
            <ViewScreenDICOM src={filePreviewData} />
          </Suspense>
        </StyledPreviewWrapper>
      );
    }

    case 'PDF': {
      return (
        <StyledPreviewWrapper data-testid="file-viewer-pdf">
          <iframe
            title="PDF preview"
            style={iframeStyles}
            src={filePreviewData}
          />
        </StyledPreviewWrapper>
      );
    }

    case 'JPEG':
    case 'PNG': {
      return (
        <StyledPreviewWrapper data-testid="file-viewer-img">
          <iframe
            title="Image preview"
            src={filePreviewData}
            style={iframeStyles}
          />
        </StyledPreviewWrapper>
      );
    }

    default: {
      return (
        <ViewerScreenMessage
          dataTestId="file-viewer-no-preview"
          title="Preview is not supported for this file type"
        />
      );
    }
  }
}
